import { useForm } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { getFlagIcon } from "@/helpers/flags";
import { createRequiredStringRule } from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import type { Languages } from "@/translations";
import { useTranslation } from "@/translations";

const NAME_MAX_LENGTH = 40;
const DESCRIPTION_MAX_LENGTH = 140;

export function ResidentGroupForm({
  contentLanguageId,
  defaultValues,
  isEditMode,
  isSubmitting,
  onSubmit,
}: {
  contentLanguageId: Languages | undefined;
  defaultValues: Partial<CreateOrEditResidentGroupFormValues>;
  isEditMode: boolean;
  isSubmitting: boolean;
  onSubmit: (data: CreateOrEditResidentGroupFormValues) => void;
}): React.ReactNode {
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const formMethods = useForm<CreateOrEditResidentGroupFormValues>({
    mode: "onChange",
    defaultValues: defaultValues,
  });

  const flagIcon = getFlagIcon({
    languageId: contentLanguageId || sessionUser.uiLanguage.id,
    width: 16,
  });

  return (
    <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
      <Form.Body>
        <FormField label={t("page.create-resident-group.form.name")} actions={flagIcon} required>
          <FormInput<CreateOrEditResidentGroupFormValues, "name">
            name="name"
            placeholder={t("page.create-resident-group.form.name.placeholder")}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.create-resident-group.form.name"),
              },
              maxLength: {
                message: t("components.form.error.max-length", {
                  length: NAME_MAX_LENGTH,
                }),
                value: NAME_MAX_LENGTH,
              },
            }}
          />
        </FormField>
        <FormField label={t("page.create-resident-group.form.description")} actions={flagIcon} required>
          <FormTextArea<CreateOrEditResidentGroupFormValues, "description">
            name="description"
            placeholder={t("page.create-resident-group.form.description.placeholder")}
            rules={{
              validate: {
                required: createRequiredStringRule(t, "page.create-resident-group.form.description"),
              },
              maxLength: {
                message: t("components.form.error.max-length", {
                  length: DESCRIPTION_MAX_LENGTH,
                }),
                value: DESCRIPTION_MAX_LENGTH,
              },
            }}
          />
        </FormField>
        <FormField label={t("page.create-resident-group.form.icon")} required>
          <FormImageInput<CreateOrEditResidentGroupFormValues, "image">
            name="image"
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.create-resident-group.form.icon"),
              }),
            }}
          />
        </FormField>
      </Form.Body>
      <Form.Footer
        renderPrimaryAction={() => (
          <Button type="submit" isLoading={isSubmitting} data-testid="submit-btn">
            {isEditMode ? t("page.create-resident-group.edit.submit") : t("page.create-resident-group.create.submit")}
          </Button>
        )}
      />
    </Form.Root>
  );
}

export interface CreateOrEditResidentGroupFormValues {
  name: string;
  description: string;
  image: FormImage[];
}
